/**
 * Calendar module — Phase G3C-A frontend helpers.
 *
 * Shared utilities for chip/badge copy, event-date formatting, and
 * canonical `event_source × event_type` presentation. Kept in a
 * plain JS module so unit tests and multiple UI surfaces (list view,
 * month view, popover, drawer) can share one source of truth.
 */

// Voice-approved copy for the source axis (per user's CP2 brief).
// Grant is the only source that fires today; Mortgage / Funding /
// Credit ship as greyed-out chips with a factual tooltip — NOT the
// timeline / maturity phrases banned by the G2A voice guardrail set.
export const SOURCE_META = {
  grant: {
    label: "Grant",
    tooltip: "Deadlines and renewal windows from your saved grants.",
    active: true,
  },
  mortgage: {
    label: "Mortgage",
    tooltip: "Grant events only for now",
    active: false,
  },
  funding: {
    label: "Funding",
    tooltip: "Grant events only for now",
    active: false,
  },
  credit: {
    label: "Credit",
    tooltip: "Grant events only for now",
    active: false,
  },
  manual: {
    label: "My reminders",
    tooltip: "Reminders you created manually.",
    active: true,
  },
};

// event_type → display metadata. The `dotClass` values drive the
// per-day dot indicator in the month grid; the `chipClass` values
// drive the pill on each list row and popover header. Kept as
// hex-with-alpha strings so the styles stay inside the dark v2
// theme without hard-coded Tailwind classes.
export const TYPE_META = {
  grant_deadline: {
    label: "Deadline",
    tooltip: "Application deadline for a saved grant.",
    dotColor: "rgb(167,139,250)", // v2-accent
    chipBg: "rgba(167,139,250,0.14)",
    chipBorder: "rgba(167,139,250,0.45)",
    chipText: "rgb(167,139,250)",
  },
  grant_renewal: {
    label: "Renewal",
    tooltip: "Annual grant window reopens ~30 days from this date.",
    dotColor: "rgb(56,189,248)", // sky-400
    chipBg: "rgba(56,189,248,0.12)",
    chipBorder: "rgba(56,189,248,0.42)",
    chipText: "rgb(56,189,248)",
  },
  document_expiry: {
    label: "Refresh",
    tooltip:
      "A document referenced by a saved grant is past its typical freshness window.",
    dotColor: "rgb(251,191,36)", // amber-400
    chipBg: "rgba(251,191,36,0.10)",
    chipBorder: "rgba(251,191,36,0.42)",
    chipText: "rgb(251,191,36)",
  },
  manual_reminder: {
    label: "Reminder",
    tooltip: "A reminder you created.",
    dotColor: "rgb(148,163,184)", // slate-400
    chipBg: "rgba(148,163,184,0.10)",
    chipBorder: "rgba(148,163,184,0.35)",
    chipText: "rgb(148,163,184)",
  },
};

export const STATUS_META = {
  active: { label: "Active" },
  dismissed: { label: "Dismissed" },
};

// Group calendar items by their local calendar day (YYYY-MM-DD) —
// used to place day-dots on the month grid.
export function groupByDate(items) {
  const buckets = {};
  for (const it of items) {
    const key = dateKey(new Date(it.event_date));
    if (!buckets[key]) buckets[key] = [];
    buckets[key].push(it);
  }
  return buckets;
}

// Local YYYY-MM-DD (avoids UTC-off-by-one on client display).
export function dateKey(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// Human display for a calendar item's `event_date`. Uses the user's
// locale so 2026-06-24 renders as "Jun 24, 2026" for US-English and
// "24 Jun 2026" for en-GB without a translation table.
export function formatEventDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

// "in 12 days" / "3 days ago" — used on list rows for at-a-glance
// urgency. Kept intentionally short (< 15 chars) so it never wraps
// the row on narrow viewports.
export function formatRelative(iso, now = new Date()) {
  const target = new Date(iso);
  const diffMs = target.getTime() - now.getTime();
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Tomorrow";
  if (diffDays === -1) return "Yesterday";
  if (diffDays > 0) return `In ${diffDays} days`;
  return `${Math.abs(diffDays)} days ago`;
}

// Build the "View source" affordance destination for a list row or
// popover. Grant-sourced events route to /grants/{id}; document
// expiries route to /documents/{doc_id}; manual events have no
// source page (returns null).
export function sourceLinkFor(event) {
  if (event.event_type === "document_expiry") {
    return { to: `/documents/${event.source_ref}`, label: "View document" };
  }
  if (
    event.event_type === "grant_deadline" ||
    event.event_type === "grant_renewal"
  ) {
    return event.source_ref
      ? { to: `/grants/${event.source_ref}`, label: "View grant" }
      : null;
  }
  return null;
}

// Default active-source filter — Grant is the only fully-live source
// today, plus Manual so users see their own reminders. Mortgage /
// Funding / Credit chips are shown as greyed-out (per user brief)
// but never selected.
export const DEFAULT_SOURCES = ["grant", "manual"];
export const DEFAULT_TYPES = [
  "grant_deadline",
  "grant_renewal",
  "document_expiry",
  "manual_reminder",
];
